import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Info, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const ToastContext = createContext();

// Hook para usar desde cualquier componente
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast debe usarse dentro de ToastProvider');
  }
  return context;
}

// Provider que envuelve la app
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', action = null) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, action }]);

    // Auto-remover. Los avisos de PROBLEMA duran más que los de éxito: el de
    // éxito confirma algo que el usuario acaba de hacer y ya sabe, el de
    // problema le explica por qué NO pasó lo que esperaba, y si se borra antes
    // de que lo lea se queda creyendo que el botón no funciona.
    const duration = action ? 5000 : (type === 'warning' || type === 'danger' ? 6000 : 3500);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/* Renderizar toasts */}
      {/*
        ─── ABAJO Y AL CENTRO, NO ARRIBA A LA DERECHA (2026-08-07) ───────────
        Estaba fijo en `top: 60px; right: 16px`. Medido en producción, en el alta
        de producto a 1440x900: el aviso nacía en y=60 y el botón que lo dispara
        estaba en y=1443, o sea 1383px más abajo, en una ventana de 900px. El
        botón ni entra en la primera pantalla: hay que bajar para pulsarlo, y
        justo entonces el aviso aparece a más de pantalla y media POR ENCIMA de
        donde mira el usuario, y se borra solo a los pocos segundos.

        No era "difícil de ver": era invisible salvo en un monitor de 1443px de
        alto. Y el síntoma que produce es el peor posible, porque no parece un
        error: parece que el botón está muerto.

        Se arregla aquí, en el contenedor, y no formulario por formulario: el
        patrón `showToast(...); return` está repartido por decenas de páginas.

        Los 100px del fondo dejan libre el BottomNav y las barras de acción de
        las pantallas (que viven entre 72 y 92px), para no repetir de paso el
        problema que este mismo cambio viene a resolver.
      */}
      <div style={{
        position: 'fixed',
        bottom: 'calc(100px + env(safe-area-inset-bottom))',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: '10px',
        alignItems: 'center',
        pointerEvents: 'none',
        width: 'calc(100% - 32px)',
        maxWidth: '380px'
      }}>
        {toasts.map((toast) => (
          <ToastItem
            key={toast.id}
            toast={toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// Componente individual de toast
function ToastItem({ toast, onClose }) {
  const types = {
    info: { bg: '#3B82F6', Icon: Info },
    success: { bg: '#10B981', Icon: CheckCircle },
    warning: { bg: '#F59E0B', Icon: AlertTriangle },
    danger: { bg: '#EF4444', Icon: XCircle }
  };

  const style = types[toast.type] || types.info;

  return (
    <div
      onClick={toast.action ? () => { toast.action.onClick(); onClose(); } : undefined}
      style={{
        backgroundColor: style.bg,
        color: 'white',
        padding: '12px 16px',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: '280px',
        maxWidth: '100%',
        pointerEvents: 'auto',
        animation: 'toastSlideIn 0.3s ease',
        cursor: toast.action ? 'pointer' : 'default',
      }}>
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
      <span style={{ display: 'flex', flexShrink: 0 }}><style.Icon size={20} color="white" /></span>
      <span style={{ fontSize: '14px', fontWeight: '500', flex: 1 }}>
        {toast.message}
      </span>
      {toast.action && (
        <span style={{
          fontSize: '13px', fontWeight: '700',
          backgroundColor: 'rgba(255,255,255,0.25)',
          padding: '4px 10px', borderRadius: '8px',
          whiteSpace: 'nowrap',
        }}>
          {toast.action.label}
        </span>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{
          backgroundColor: 'transparent',
          border: 'none',
          color: 'white',
          fontSize: '18px',
          cursor: 'pointer',
          padding: '0 4px',
          opacity: 0.8
        }}
      >
        ×
      </button>
    </div>
  );
}
